import { ApiError } from "@/lib/api/client";
import { isLocalDemoRequested } from "@/lib/api/demoMode";
import { onSessionInvalidated, sessionOwnerKey, setSessionIdentity, suspendSession, type SessionOwner } from "@/lib/api/session";
import { clearOwnedPrivateStorage } from "@/lib/privateStorage";
import { accountRequest, getAccountConfig, getAccountSession } from "../api/account";
import { ACCOUNT_EVENT_KEY, ACCOUNT_LOCK_KEY, hasAccountLock, reconcileSharedUnlock, setAccountLock } from "../storage/lock";
import { accountSessionSchema, type AccountSession } from "./schema";

export interface AccountState {
  status: "checking" | "error" | "login" | "password" | "ready" | "legacy";
  session: AccountSession | null;
  busy: boolean;
  notice: "" | "signedOut" | "logoutFailed" | "passwordChanged";
}
/** 账户生命周期独立于业务缓存；跨标签消息只触发服务端重新确认。 */
export class AccountController {
  private state: AccountState = { status: "checking", session: null, busy: false, notice: "" };
  private listeners = new Set<() => void>();
  private request: AbortController | null = null;
  private epoch = 0;
  private enabled: boolean | undefined;
  private channel: BroadcastChannel | null = null;
  private removeInvalidator: (() => void) | undefined;
  private storageOwner: SessionOwner = null;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getSnapshot = () => this.state;
  private update(change: Partial<AccountState>) {
    this.state = { ...this.state, ...change };
    this.listeners.forEach((listener) => listener());
  }
  private begin() {
    this.request?.abort();
    this.request = new AbortController();
    const epoch = ++this.epoch;
    return { signal: AbortSignal.any([this.request.signal, AbortSignal.timeout(15_000)]), current: () => epoch === this.epoch };
  }
  private accept(session: AccountSession) {
    const owner = { userId: session.user.id, workspaceId: session.user.workspace_id, csrfToken: session.csrf_token };
    if (sessionOwnerKey(this.storageOwner) !== sessionOwnerKey(owner)) clearOwnedPrivateStorage(this.storageOwner);
    this.storageOwner = owner;
    setSessionIdentity(owner);
    this.update({ session, status: session.user.must_change_password ? "password" : "ready", busy: false, notice: "" });
  }
  private lock(notice: AccountState["notice"] = "signedOut", clearStorage = true) {
    this.request?.abort(); ++this.epoch;
    if (clearStorage) { clearOwnedPrivateStorage(this.storageOwner); this.storageOwner = null; }
    setSessionIdentity(null);
    this.update({ status: "login", session: null, busy: false, notice });
  }
  private broadcast(type: "changed" | "signed-in" = "changed") {
    this.channel?.postMessage(type);
    try { localStorage.setItem(ACCOUNT_EVENT_KEY, `${type}:${crypto.randomUUID()}`); } catch { /* BroadcastChannel 可独立工作。 */ }
  }
  private externalChange = (event?: MessageEvent<unknown>) => {
    if (this.enabled !== true) return;
    if (event?.data === "signed-in") reconcileSharedUnlock();
    void this.refresh();
  };
  private storageChange = (event: StorageEvent) => {
    if (this.enabled !== true) return;
    if ((event.key === ACCOUNT_LOCK_KEY && event.newValue === null)
      || (event.key === ACCOUNT_EVENT_KEY && event.newValue?.startsWith("signed-in:"))) reconcileSharedUnlock();
    if (event.key === ACCOUNT_EVENT_KEY || event.key === ACCOUNT_LOCK_KEY) this.externalChange();
  };
  start() {
    setSessionIdentity(null);
    this.removeInvalidator = onSessionInvalidated(() => {
      setAccountLock(true); this.lock(); this.broadcast();
    });
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel("oransim-account");
      this.channel.onmessage = this.externalChange;
    }
    window.addEventListener("storage", this.storageChange);
    void this.refresh();
  }
  stop() {
    this.request?.abort(); ++this.epoch;
    this.removeInvalidator?.();
    this.channel?.close(); this.channel = null;
    window.removeEventListener("storage", this.storageChange);
  }
  suspend() {
    if (this.enabled === false) return;
    this.request?.abort(); ++this.epoch;
    suspendSession();
    if (this.state.session) this.update({ status: "checking", busy: false });
  }
  async refresh() {
    this.suspend();
    const request = this.begin();
    this.update({ status: "checking", busy: false });
    try {
      if (this.enabled === undefined) {
        try { this.enabled = (await getAccountConfig(request.signal)).enabled; }
        catch (error) {
          if (!request.current()) return;
          if (!isLocalDemoRequested()) throw error;
          this.enabled = false;
        }
      }
      if (!request.current()) return;
      if (!this.enabled) { setSessionIdentity("legacy"); this.update({ status: "legacy", session: null }); return; }
      if (hasAccountLock()) { this.lock(); return; }
      const session = await getAccountSession(request.signal);
      if (request.current()) this.accept(session);
    } catch (error) {
      if (!request.current()) return;
      if (error instanceof ApiError && error.status === 401 && this.enabled) this.lock("");
      else this.update({ status: "error" });
    }
  }
  async login(username: string, password: string) {
    if (this.state.busy || this.enabled !== true) return;
    const request = this.begin();
    this.update({ busy: true, notice: "" });
    try {
      const session = accountSessionSchema.parse(await accountRequest("login", request.signal, { username, password }));
      if (!request.current()) return;
      setAccountLock(false);
      this.accept(session);
      this.broadcast("signed-in");
    } catch (error) { if (request.current()) throw error; }
    finally { if (request.current()) this.update({ busy: false }); }
  }
  async logout() {
    const csrf = this.state.session?.csrf_token;
    setAccountLock(true); this.lock(); this.broadcast();
    if (!csrf) return;
    const request = this.begin();
    this.update({ busy: true });
    try { await accountRequest("logout", request.signal, {}, csrf); }
    catch { if (request.current()) this.update({ notice: "logoutFailed" }); }
    finally { if (request.current()) this.update({ busy: false }); }
  }
  showPassword() { if (this.state.session) { this.suspend(); this.update({ status: "password" }); } }
  cancelPassword() { if (this.state.session && !this.state.session.user.must_change_password) void this.refresh(); }
  async changePassword(current_password: string, new_password: string) {
    const session = this.state.session;
    if (!session || this.state.busy) return;
    const request = this.begin();
    this.update({ busy: true });
    try {
      await accountRequest("password", request.signal, { current_password, new_password }, session.csrf_token);
      if (!request.current()) return;
      setAccountLock(true); this.lock("passwordChanged"); this.broadcast();
    } catch (error) { if (request.current()) throw error; }
    finally { if (request.current()) this.update({ busy: false }); }
  }
}
